import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function LedgerQuery() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Data states
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [feeTransactions, setFeeTransactions] = useState([]);

  // Filter states
  const [activeTab, setActiveTab] = useState("ledger");
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [transactionType, setTransactionType] = useState("all");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    loadLedgerAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "journals") loadJournalEntries();
    if (activeTab === "fees") loadFeeTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateFilter]);

  const loadLedgerAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Accounting/ledger-accounts");
      setLedgerAccounts(res.data);
    } catch (err) {
      setError("Failed to load ledger accounts");
    } finally {
      setLoading(false);
    }
  };

  const loadAccountTransactions = async (account) => {
    setLoading(true);
    try {
      // Load all journal entries and filter by account code
      const res = await api.get(`/Accounting/journal-entries`, {
        params: { startDate: dateFilter.startDate, endDate: dateFilter.endDate }
      });
      
      // Filter entries that involve this account by accountCode (AccountNumber)
      const accountCode = account.accountCode || account.accountNumber;
      const filtered = res.data.filter(entry => 
        entry.lines?.some(line => 
          line.accountCode === accountCode || 
          line.accountNumber === accountCode
        )
      );
      
      // Transform to show only relevant line items for this account
      const accountTransactions = [];
      filtered.forEach(entry => {
        entry.lines?.forEach(line => {
          if (line.accountCode === accountCode || line.accountNumber === accountCode) {
            accountTransactions.push({
              id: entry.id,
              date: entry.createdAt,
              journalNumber: entry.journalNumber,
              description: entry.description || line.description,
              transactionType: entry.transactionType,
              debit: line.debit || 0,
              credit: line.credit || 0,
              accountCode: line.accountCode,
              accountName: line.accountName
            });
          }
        });
      });
      
      setTransactions(accountTransactions);
    } catch (err) {
      console.error("Failed to load transactions:", err);
      setError("Failed to load transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadJournalEntries = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Accounting/journal-entries", {
        params: { startDate: dateFilter.startDate, endDate: dateFilter.endDate }
      });
      setJournalEntries(res.data);
    } catch (err) {
      setError("Failed to load journal entries");
    } finally {
      setLoading(false);
    }
  };

  const loadFeeTransactions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Fee/transactions", {
        params: { startDate: dateFilter.startDate, endDate: dateFilter.endDate }
      });
      setFeeTransactions(res.data);
    } catch (err) {
      setError("Failed to load fee transactions");
    } finally {
      setLoading(false);
    }
  };

  const selectAccount = (account) => {
    setSelectedAccount(account);
    loadAccountTransactions(account);
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  const formatDateTime = (date) => new Date(date).toLocaleString("en-NG", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  // Filter and paginate data
  const filterData = (data) => {
    let filtered = data;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        JSON.stringify(item).toLowerCase().includes(term)
      );
    }
    
    if (transactionType !== "all") {
      filtered = filtered.filter(item => 
        item.transactionType?.toLowerCase() === transactionType.toLowerCase() ||
        item.type?.toLowerCase() === transactionType.toLowerCase()
      );
    }
    
    return filtered;
  };

  const paginateData = (data) => {
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  };

  const getTotalPages = (data) => Math.ceil(data.length / pageSize);

  const getAccountTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "asset": return "#28a745";
      case "liability": return "#dc3545";
      case "equity": return "#6c757d";
      case "revenue": return "#007bff";
      case "expense": return "#fd7e14";
      default: return "#6c757d";
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "agentdeposit": return { bg: "#d4edda", color: "#155724" };
      case "agentwithdrawal": return { bg: "#f8d7da", color: "#721c24" };
      case "capitalinvestment": return { bg: "#cce5ff", color: "#004085" };
      case "loadfloat": return { bg: "#fff3cd", color: "#856404" };
      case "transfer": return { bg: "#e2e3e5", color: "#383d41" };
      default: return { bg: "#e9ecef", color: "#495057" };
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Ledger Query</h1>
        <div style={styles.headerButtons}>
          <button onClick={() => navigate("/accounting")} style={styles.btnOutline}>← Accounting</button>
          <button onClick={() => navigate("/admin")} style={styles.btnOutline}>← Admin</button>
        </div>
      </div>

      {error && <div style={styles.error}>{error} <button onClick={() => setError("")}>✕</button></div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: "ledger", label: "📚 Ledger Accounts", icon: "📚" },
          { id: "journals", label: "📝 Journal Entries", icon: "📝" },
          { id: "fees", label: "💰 Fee Transactions", icon: "💰" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setCurrentPage(1); }}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={styles.filterCard}>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>From:</label>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
              style={styles.dateInput}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>To:</label>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
              style={styles.dateInput}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Search:</label>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Type:</label>
            <select
              value={transactionType}
              onChange={(e) => { setTransactionType(e.target.value); setCurrentPage(1); }}
              style={styles.select}
            >
              <option value="all">All Types</option>
              <option value="AgentDeposit">Agent Deposit</option>
              <option value="AgentWithdrawal">Agent Withdrawal</option>
              <option value="CapitalInvestment">Capital Investment</option>
              <option value="LoadFloat">Load Float</option>
              <option value="Transfer">Transfer</option>
            </select>
          </div>
          <button onClick={() => { loadJournalEntries(); loadFeeTransactions(); }} style={styles.btnPrimary}>
            🔍 Filter
          </button>
        </div>
      </div>

      {loading && <div style={styles.loading}>Loading...</div>}

      {/* Ledger Accounts Tab */}
      {activeTab === "ledger" && !loading && (
        <div style={styles.splitView}>
          {/* Account List */}
          <div style={styles.accountList}>
            <h3 style={styles.sectionTitle}>Ledger Accounts</h3>
            <div style={styles.accountGrid}>
              {ledgerAccounts.map((account) => (
                <div
                  key={account.id}
                  onClick={() => selectAccount(account)}
                  style={{
                    ...styles.accountCard,
                    borderLeft: `4px solid ${getAccountTypeColor(account.accountType)}`,
                    ...(selectedAccount?.id === account.id ? styles.accountCardSelected : {})
                  }}
                >
                  <div style={styles.accountHeader}>
                    <span style={styles.accountNumber}>{account.accountNumber}</span>
                    <span style={{
                      ...styles.badge,
                      backgroundColor: getAccountTypeColor(account.accountType),
                      color: "white"
                    }}>
                      {account.accountType}
                    </span>
                  </div>
                  <div style={styles.accountName}>{account.accountName}</div>
                  <div style={styles.accountBalance}>{formatCurrency(account.balance)}</div>
                  {account.agentName && (
                    <div style={styles.accountAgent}>Agent: {account.agentName}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Account Transactions */}
          <div style={styles.transactionPanel}>
            {selectedAccount ? (
              <>
                <div style={styles.selectedAccountHeader}>
                  <h3 style={styles.sectionTitle}>
                    {selectedAccount.accountName}
                    <span style={styles.accountNumberSmall}>({selectedAccount.accountNumber})</span>
                  </h3>
                  <div style={styles.selectedAccountBalance}>
                    Balance: <strong>{formatCurrency(selectedAccount.balance)}</strong>
                  </div>
                </div>

                <div style={styles.transactionList}>
                  {transactions.length > 0 ? (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Date</th>
                          <th style={styles.th}>Type</th>
                          <th style={styles.th}>Description</th>
                          <th style={styles.th}>Debit</th>
                          <th style={styles.th}>Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx, i) => (
                          <tr key={i}>
                            <td style={styles.td}>{formatDateTime(tx.date || tx.transactionDate || tx.createdAt)}</td>
                            <td style={styles.td}>
                              <span style={{
                                ...styles.typeBadge,
                                ...getTransactionTypeColor(tx.transactionType || tx.type)
                              }}>
                                {tx.transactionType || tx.type}
                              </span>
                            </td>
                            <td style={styles.td}>{tx.description}</td>
                            <td style={{ ...styles.td, color: "#dc3545", fontWeight: (tx.debit || tx.debitAmount) > 0 ? "bold" : "normal" }}>
                              {(tx.debit || tx.debitAmount) > 0 ? formatCurrency(tx.debit || tx.debitAmount) : "-"}
                            </td>
                            <td style={{ ...styles.td, color: "#28a745", fontWeight: (tx.credit || tx.creditAmount) > 0 ? "bold" : "normal" }}>
                              {(tx.credit || tx.creditAmount) > 0 ? formatCurrency(tx.credit || tx.creditAmount) : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={styles.noData}>No transactions found for this account in the selected date range.</div>
                  )}
                </div>
              </>
            ) : (
              <div style={styles.noSelection}>
                <div style={styles.noSelectionIcon}>📋</div>
                <p>Select a ledger account to view its transaction history</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Journal Entries Tab */}
      {activeTab === "journals" && !loading && (
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>
            Journal Entries
            <span style={styles.countBadge}>{filterData(journalEntries).length} entries</span>
          </h3>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Journal #</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginateData(filterData(journalEntries)).map((entry) => (
                <React.Fragment key={entry.id}>
                  <tr style={styles.journalRow}>
                    <td style={styles.td}>
                      <strong>{entry.journalNumber}</strong>
                    </td>
                    <td style={styles.td}>{formatDateTime(entry.transactionDate)}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.typeBadge,
                        ...getTransactionTypeColor(entry.transactionType)
                      }}>
                        {entry.transactionType}
                      </span>
                    </td>
                    <td style={styles.td}>{entry.description}</td>
                    <td style={{ ...styles.td, fontWeight: "bold" }}>{formatCurrency(entry.totalAmount)}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: entry.status === "Posted" ? "#28a745" : "#ffc107"
                      }}>
                        {entry.status}
                      </span>
                    </td>
                  </tr>
                  {entry.lines && entry.lines.length > 0 && (
                    <tr>
                      <td colSpan="6" style={styles.linesCell}>
                        <table style={styles.linesTable}>
                          <thead>
                            <tr>
                              <th style={styles.linesTh}>Account</th>
                              <th style={styles.linesTh}>Description</th>
                              <th style={styles.linesTh}>Debit</th>
                              <th style={styles.linesTh}>Credit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entry.lines.map((line, i) => (
                              <tr key={i}>
                                <td style={styles.linesTd}>
                                  <code>{line.accountNumber}</code> - {line.accountName}
                                </td>
                                <td style={styles.linesTd}>{line.description}</td>
                                <td style={{ ...styles.linesTd, color: "#dc3545" }}>
                                  {line.debitAmount > 0 ? formatCurrency(line.debitAmount) : ""}
                                </td>
                                <td style={{ ...styles.linesTd, color: "#28a745" }}>
                                  {line.creditAmount > 0 ? formatCurrency(line.creditAmount) : ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {getTotalPages(filterData(journalEntries)) > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={styles.pageBtn}
              >
                ← Previous
              </button>
              <span style={styles.pageInfo}>
                Page {currentPage} of {getTotalPages(filterData(journalEntries))}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(getTotalPages(filterData(journalEntries)), p + 1))}
                disabled={currentPage === getTotalPages(filterData(journalEntries))}
                style={styles.pageBtn}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Fee Transactions Tab */}
      {activeTab === "fees" && !loading && (
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>
            Fee Transactions
            <span style={styles.countBadge}>{filterData(feeTransactions).length} transactions</span>
          </h3>

          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Total Fees Collected</div>
              <div style={styles.summaryValue}>
                {formatCurrency(feeTransactions.reduce((sum, t) => sum + (t.feeAmount || 0), 0))}
              </div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Company Share</div>
              <div style={{ ...styles.summaryValue, color: "#007bff" }}>
                {formatCurrency(feeTransactions.reduce((sum, t) => sum + ((t.feeAmount || 0) - (t.agentCommission || 0)), 0))}
              </div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Agent Commissions</div>
              <div style={{ ...styles.summaryValue, color: "#28a745" }}>
                {formatCurrency(feeTransactions.reduce((sum, t) => sum + (t.agentCommission || 0), 0))}
              </div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Transaction Count</div>
              <div style={styles.summaryValue}>{feeTransactions.length}</div>
            </div>
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Transaction Amount</th>
                <th style={styles.th}>Fee Amount</th>
                <th style={styles.th}>Company Share</th>
                <th style={styles.th}>Agent Commission</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginateData(filterData(feeTransactions)).map((tx) => (
                <tr key={tx.id}>
                  <td style={styles.td}>{formatDateTime(tx.createdAt)}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.typeBadge,
                      ...getTransactionTypeColor(tx.transactionFee?.transactionType)
                    }}>
                      {tx.transactionFee?.transactionType || "N/A"}
                    </span>
                  </td>
                  <td style={styles.td}>{formatCurrency(tx.transactionAmount)}</td>
                  <td style={{ ...styles.td, fontWeight: "bold" }}>{formatCurrency(tx.feeAmount)}</td>
                  <td style={{ ...styles.td, color: "#007bff" }}>
                    {formatCurrency((tx.feeAmount || 0) - (tx.agentCommission || 0))}
                  </td>
                  <td style={{ ...styles.td, color: "#28a745" }}>{formatCurrency(tx.agentCommission)}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      backgroundColor: tx.status === "Completed" ? "#28a745" : "#ffc107"
                    }}>
                      {tx.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {getTotalPages(filterData(feeTransactions)) > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={styles.pageBtn}
              >
                ← Previous
              </button>
              <span style={styles.pageInfo}>
                Page {currentPage} of {getTotalPages(filterData(feeTransactions))}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(getTotalPages(filterData(feeTransactions)), p + 1))}
                disabled={currentPage === getTotalPages(filterData(feeTransactions))}
                style={styles.pageBtn}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
    backgroundColor: "#f5f5f5",
    minHeight: "100vh"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "10px"
  },
  title: { fontSize: "24px", margin: 0 },
  headerButtons: { display: "flex", gap: "10px" },
  
  tabs: {
    display: "flex",
    gap: "5px",
    marginBottom: "20px",
    flexWrap: "wrap"
  },
  tab: {
    padding: "12px 20px",
    border: "none",
    backgroundColor: "#e9ecef",
    borderRadius: "8px 8px 0 0",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s"
  },
  tabActive: {
    backgroundColor: "#007bff",
    color: "white"
  },

  filterCard: {
    backgroundColor: "white",
    padding: "15px 20px",
    borderRadius: "8px",
    marginBottom: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  filterRow: {
    display: "flex",
    gap: "15px",
    alignItems: "flex-end",
    flexWrap: "wrap"
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "5px"
  },
  filterLabel: {
    fontSize: "12px",
    color: "#666",
    fontWeight: "500"
  },
  dateInput: {
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px"
  },
  searchInput: {
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    width: "200px"
  },
  select: {
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    minWidth: "150px"
  },

  splitView: {
    display: "grid",
    gridTemplateColumns: "350px 1fr",
    gap: "20px"
  },
  accountList: {
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "15px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    maxHeight: "calc(100vh - 280px)",
    overflowY: "auto"
  },
  accountGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  accountCard: {
    padding: "12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  accountCardSelected: {
    backgroundColor: "#e3f2fd",
    boxShadow: "0 0 0 2px #007bff"
  },
  accountHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "5px"
  },
  accountNumber: {
    fontFamily: "monospace",
    fontSize: "12px",
    color: "#666"
  },
  accountName: {
    fontWeight: "600",
    fontSize: "14px",
    marginBottom: "5px"
  },
  accountBalance: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#333"
  },
  accountAgent: {
    fontSize: "11px",
    color: "#666",
    marginTop: "5px"
  },

  transactionPanel: {
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  selectedAccountHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "15px",
    borderBottom: "1px solid #eee"
  },
  accountNumberSmall: {
    fontSize: "12px",
    color: "#666",
    fontWeight: "normal",
    marginLeft: "10px"
  },
  selectedAccountBalance: {
    fontSize: "14px",
    color: "#666"
  },
  transactionList: {
    maxHeight: "calc(100vh - 380px)",
    overflowY: "auto"
  },
  noSelection: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#666"
  },
  noSelectionIcon: {
    fontSize: "48px",
    marginBottom: "15px"
  },
  noData: {
    textAlign: "center",
    padding: "40px",
    color: "#666"
  },

  card: {
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  sectionTitle: {
    margin: "0 0 15px 0",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  countBadge: {
    fontSize: "12px",
    backgroundColor: "#e9ecef",
    padding: "3px 10px",
    borderRadius: "12px",
    fontWeight: "normal"
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
    marginBottom: "20px"
  },
  summaryCard: {
    backgroundColor: "#f8f9fa",
    padding: "15px",
    borderRadius: "8px",
    textAlign: "center"
  },
  summaryLabel: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "5px"
  },
  summaryValue: {
    fontSize: "20px",
    fontWeight: "bold"
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px"
  },
  th: {
    textAlign: "left",
    padding: "12px 10px",
    borderBottom: "2px solid #dee2e6",
    backgroundColor: "#f8f9fa",
    fontWeight: "600",
    position: "sticky",
    top: 0
  },
  td: {
    padding: "10px",
    borderBottom: "1px solid #eee"
  },
  journalRow: {
    backgroundColor: "#fafafa"
  },
  linesCell: {
    padding: "0 10px 15px 30px",
    backgroundColor: "#fff"
  },
  linesTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
    marginTop: "5px"
  },
  linesTh: {
    textAlign: "left",
    padding: "6px 8px",
    borderBottom: "1px solid #ddd",
    color: "#666",
    fontWeight: "500"
  },
  linesTd: {
    padding: "6px 8px",
    borderBottom: "1px solid #eee"
  },

  badge: {
    padding: "3px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
    color: "white"
  },
  typeBadge: {
    padding: "3px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "500"
  },

  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "15px",
    marginTop: "20px",
    paddingTop: "15px",
    borderTop: "1px solid #eee"
  },
  pageBtn: {
    padding: "8px 15px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "13px"
  },
  pageInfo: {
    fontSize: "13px",
    color: "#666"
  },

  btnPrimary: {
    padding: "8px 16px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500"
  },
  btnOutline: {
    padding: "8px 16px",
    backgroundColor: "white",
    color: "#007bff",
    border: "1px solid #007bff",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px"
  },

  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "10px 15px",
    borderRadius: "4px",
    marginBottom: "15px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  loading: {
    textAlign: "center",
    padding: "60px",
    color: "#666",
    fontSize: "16px"
  }
};

